'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function CeneyeBotoksUygulamasiNedirNasilYapilirPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/ceneye-botoks-uygulamasi-nedir-nasil-yapilir';
  const pageTitle = 'Çeneye Botoks Uygulaması Nedir, Nasıl Yapılır?';
  const pageDescription = 'Çeneye botoks uygulaması ne işe yarar? Çene hattını incelten, diş sıkmayı azaltan ve yüz oranlarını dengeleyen bu işlemin etkileri ve süresi.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Çeneye Botoks Uygulaması', href: '/tr/blog/ceneye-botoks-uygulamasi-nedir-nasil-yapilir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Çeneye botoks uygulaması ve V-line estetiği değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                V-Line Estetik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Çeneye Botoks Uygulaması Nedir, Nasıl Yapılır?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>30 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Çeneye botoks, hem estetik görünümü hem de fonksiyonel problemleri düzeltmek için uygulanan etkili bir yöntemdir. Botulinum toksini, çene kaslarının (özellikle masseter kası) aktivitesini geçici olarak azaltarak hem yüz hattında incelme hem de diş sıkma (bruksizm) gibi sorunlarda rahatlama sağlar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Son yıllarda "V-Line botoks" olarak da bilinen bu işlem, daha ince, oval ve orantılı bir yüz formu isteyen kişiler tarafından sıkça tercih edilmektedir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Çeneye Botoks Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Çeneye botoks, özellikle çiğneme kası olarak bilinen masseter kasına küçük dozlarda botulinum toksin enjeksiyonu yapılması işlemidir. Bu kas, aşırı kullanıldığında yüzün alt kısmını geniş gösterir ve zamanla kare bir görünüm oluşturur.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks sayesinde bu kasın aşırı kasılması önlenir; böylece hem çene hattı daralır, hem de diş sıkmaya bağlı ağrılar azalır.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 font-medium">
                  💡 Kısacası: Çene botoksu sadece estetik bir işlem değildir — aynı zamanda fonksiyonel rahatlama sağlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Çeneye Botoks Kimlere Uygulanır?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">Uygun Adaylar:</h3>
                  <ul className="list-disc list-inside text-green-800 space-y-2">
                    <li>Geceleri diş sıkan veya gıcırdatan kişiler (bruksizm)</li>
                    <li>Geniş veya kare yüz hattına sahip olanlar</li>
                    <li>Çene hattını inceltmek isteyenler</li>
                    <li>Aşırı kas gelişimine bağlı yüz asimetrisi bulunanlar</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">⚠️ Uygulanmaz:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Hamile ve emziren kadınlar</li>
                    <li>Nöromüsküler hastalığı olanlar</li>
                    <li>Aktif enfeksiyonu bulunan kişiler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                İşlem, kısa sürede ve minimal ağrıyla yapılır:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-blue-800 space-y-2">
                  <li>Cilt dezenfekte edilir, anestezik krem uygulanır</li>
                  <li>Her iki masseter kasına ortalama 3–5 küçük enjeksiyon noktası belirlenir</li>
                  <li>İşlem toplam 10–15 dakika sürer</li>
                </ol>
                <div className="mt-4 space-y-2">
                  <p className="text-blue-800 font-medium">🕒 Etki 3–7 gün içinde başlar, maksimum sonuç 2–4 haftada görülür</p>
                  <p className="text-blue-800 font-medium">💎 Kalıcılık süresi genellikle 4–6 ay arasındadır</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Çeneye Botoksun Faydaları
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Fayda
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yüz hatlarını inceltir</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Geniş çene hattı daha oval hale gelir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Diş sıkmayı azaltır</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Bruksizme bağlı ağrı, diş aşınması ve çene kilitlenmesi azalır</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yüz simetrisini dengeler</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas asimetrileri yumuşatılır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Ameliyatsız çözüm</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi işlem gerektirmeden doğal sonuç verir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Etki Süresi ve Tekrarlama
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Çene botoksunun etkisi ortalama 4–6 ay sürer. Kas aktivitesi azaldıkça yüz hatları daha kalıcı biçimde incelir. Düzenli uygulamalarda (yılda 2 kez) kas hafızası değiştiği için etki süresi uzar.
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-8">
                <p className="text-yellow-800 font-medium">
                  💡 İlk kez yaptıran kişilerde hafif asimetri veya sertlik hissi normaldir; birkaç hafta içinde kaybolur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-orange-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>İlk 24 saat çene kaslarını aşırı kullanmaktan kaçının (sert yiyecek çiğnemeyin)</li>
                  <li>4 saat boyunca öne eğilmeyin veya masaj yapmayın</li>
                  <li>İlk 2 gün sauna, spor, sıcak duş önerilmez</li>
                  <li>Hafif ödem veya hassasiyet görülebilir; 1–2 gün içinde geçer</li>
                </ul>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Çeneye botoks uygulaması ve V-line yüz estetiği"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Çeneye botoks yüzü zayıflatır mı?</h3>
                  <p className="text-gray-700">Evet, çene hattı daraldığı için yüz daha ince ve V-form görünür.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">İlk uygulamada 4–6 ay, düzenli seanslarda 8–10 aya kadar sürebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Diş sıkma tamamen geçer mi?</h3>
                  <p className="text-gray-700">Kas gevşediği için sıkma büyük oranda azalır; ağrılar hafifler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Konuşma veya çiğneme zorlaşır mı?</h3>
                  <p className="text-gray-700">Hayır, doz kontrollü yapıldığında fonksiyonlar etkilenmez.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Dolgu veya botoks birlikte yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet. Çene hattı dolgusu ile birlikte yapıldığında daha keskin bir kontur elde edilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Academy of Facial Esthetics (AAFE) – "Masseter Botox: Function and Aesthetics"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – Çene ve Yüz Altı Bölge Botoks Rehberi</li>
                <li>Harvard Health Publishing – "Bruxism and Botox: A Clinical Approach"</li>
                <li>Mayo Clinic – "Botulinum Toxin in Jaw Muscle Treatment"</li>
                <li>ISAPS 2024 Global Report – "V-Line Aesthetics and Masseter Reduction Trends"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#çenebotoksu</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#masseterbotoks</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#vline</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#yüzinceltme</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#bruksizm</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botoks</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Çeneye Botoks Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">V-line estetik ve bruksizm tedavisi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Çeneye botoks uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dudaga-botoks-uygulamasi-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudağa Botoks Uygulaması Nedir?</h4>
              <p className="text-gray-600 text-sm">Lip flip tekniği ve doğal dudak belirginleştirme hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/botoks-ne-kadar-sure-gider"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">⏱️ Botoks Ne Kadar Süre Gider?</h4>
              <p className="text-gray-600 text-sm">Botoks etki süresi ve kalıcılık hakkında detaylı bilgiler.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
